import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { vibrar } from '../utils/hapticos';
import { AlertTriangle } from 'lucide-react';

/**
 * Diálogo de confirmación propio, en lugar de window.confirm: el del sistema
 * sale con un título genérico y sin distinguir la acción peligrosa. Este sigue
 * la guía de Apple: la acción destructiva en rojo y «Cancelar» como opción
 * segura (la que tiene el foco y la que vale con Esc o tocando fuera).
 *
 *   const confirmar = useConfirmar();
 *   if (!(await confirmar({ titulo: '¿Salir del plan?', mensaje: '…', accion: 'Salir' }))) return;
 *   await confirmar({ titulo: 'No se pudo guardar', accion: 'Entendido', soloAviso: true });
 */
export interface OpcionesConfirmar {
  titulo: string;
  mensaje?: string;
  /** Texto del botón que confirma (p. ej. «Quitar»). */
  accion: string;
  /** Por defecto true: el botón de la acción va en rojo. */
  destructiva?: boolean;
  /** Solo un aviso: un único botón (la acción, p. ej. «Entendido»), sin «Cancelar». */
  soloAviso?: boolean;
}

type Confirmar = (opciones: OpcionesConfirmar) => Promise<boolean>;

const ConfirmarContext = createContext<Confirmar | undefined>(undefined);

export const ConfirmarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [abierto, setAbierto] = useState<OpcionesConfirmar | null>(null);
  const responder = useRef<(si: boolean) => void>(() => {});
  const botonCancelar = useRef<HTMLButtonElement>(null);
  const botonAccion = useRef<HTMLButtonElement>(null);

  const confirmar = useCallback<Confirmar>(opciones => new Promise(resolve => {
    responder.current = resolve;
    setAbierto(opciones);
  }), []);

  const cerrar = (si: boolean) => {
    // Confirmar algo destructivo (borrar, salir del plan…) se nota en la mano
    if (si && !abierto?.soloAviso && (abierto?.destructiva ?? true)) vibrar.fuerte();
    setAbierto(null);
    responder.current(si);
  };

  useEffect(() => {
    if (!abierto) return;
    // El foco en la opción segura: «Cancelar» (o el único botón de un aviso)
    (botonCancelar.current ?? botonAccion.current)?.focus();
    // En fase de captura y sin propagar: Esc cierra este diálogo, no la ventana de debajo
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setAbierto(null);
      responder.current(false);
    };
    window.addEventListener('keydown', alPulsar, true);
    return () => window.removeEventListener('keydown', alPulsar, true);
  }, [abierto]);

  const destructiva = !abierto?.soloAviso && (abierto?.destructiva ?? true);

  return (
    <ConfirmarContext.Provider value={confirmar}>
      {children}
      {abierto && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center capa-modal"
          onClick={() => cerrar(false)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="confirmar-titulo"
            aria-describedby={abierto.mensaje ? 'confirmar-mensaje' : undefined}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-5 max-h-full overflow-y-auto">
            <div className="flex items-start gap-3">
              {destructiva && (
                <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              )}
              <div className="min-w-0">
                <h2 id="confirmar-titulo" className="font-bold text-gray-900 dark:text-white">{abierto.titulo}</h2>
                {abierto.mensaje && (
                  <p id="confirmar-mensaje" className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                    {abierto.mensaje}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              {!abierto.soloAviso && (
              <button ref={botonCancelar} type="button" onClick={() => cerrar(false)}
                className="flex-1 min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600
                           text-gray-700 dark:text-gray-300 font-semibold text-sm
                           hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                Cancelar
              </button>
              )}
              <button ref={botonAccion} type="button" onClick={() => cerrar(true)}
                className={`flex-1 min-h-[44px] px-4 rounded-lg font-semibold text-sm text-white transition ${
                  destructiva ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {abierto.accion}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmarContext.Provider>
  );
};

export const useConfirmar = () => {
  const context = useContext(ConfirmarContext);
  if (!context) throw new Error('useConfirmar debe usarse dentro de ConfirmarProvider');
  return context;
};
